import { UIElement } from '../ui-element';
import { UIStep } from './ui-step';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Children } from '../../decorators/child.decorator';
import styles from './ui-steps.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UISteps
 * @element ui-steps
 * @classdesc Represents a class for <code>ui-steps</code> element.
 * Draws steps horizontally in desktop, vertically in mobile.
 * @property {number} [activeStep] {@attr active-step} Which step is activated.
 * @property {NodeList<UIStep>} steps Shortcut to ui-steps collection.
 * @slot {@type "ui-step"}
 * @example
 * <ui-steps>
 *    <ui-step label="First step"></ui-step>
 *    <ui-step label="Second step"></ui-step>
 *    <ui-step label="Finished step" completed="true"></ui-step>
 * </ui-steps>
 */
@Component({ tag: 'ui-steps', styles })
export class UISteps extends UIElement {
    @Attribute(Number) activeStep: number = 0;

    @Children('ui-step') steps!: NodeListOf<UIStep>;

    /**
     * Sets active step by selectedIndex if previously no active steps set.
     */
    activateStep(): void {
        const activeSteps = this.queryChildren('ui-step[active]');
        if (activeSteps.length > 0) {
            return;
        }
        const step = this.steps[this.activeStep];
        if (step) {
            step.active = true;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.setAttribute('role', 'list');
        this.updateElement({
            classList: {
                'ui-steps__list': true,
            },
            children: this.detachChildNodes(),
        });
        this.activateStep();
    }
}
